<template>
  <div class="flex h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 flex-col overflow-hidden">
    <Header @sidebarToggle="toggleApiSidebar" :show-logo="true" class="sticky top-0 z-50 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 shadow-sm" />

    <div class="flex flex-1 overflow-hidden relative">
      <aside 
        class="w-64 h-full flex flex-col bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 transition-all duration-300 ease-in-out shrink-0 z-40"
        :class="{ '-ml-64 lg:ml-0': showApiSidebar === false }"
      >
        <div class="p-4 border-b border-slate-200 dark:border-slate-700">
          <div class="relative">
            <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </span>
            <input
              type="text"
              v-model="searchQuery"
              placeholder="Search API..."
              class="input input-sm input-bordered w-full pl-10 bg-slate-50 dark:bg-slate-700 focus:ring-2 focus:ring-primary/20 transition-all text-sm"
            />
          </div>
        </div>

        <nav class="flex-1 overflow-y-auto p-5 custom-scrollbar">
          <div v-if="collectionData?.info !== undefined && collectionData?.info !== null" class="mb-6">
            <button 
              @click="selectItem({ type: 'info', data: collectionData.info })" 
              class="flex items-center w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-slate-100 dark:hover:bg-slate-700"
              :class="isActive('info') === true ? 'bg-primary/10 text-primary dark:bg-primary/20' : 'text-slate-600 dark:text-slate-400'"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Overview
            </button>
          </div>
          
          <div v-for="folder in filteredCollection" :key="folder.name" class="mb-4">
            <h4 class="px-3 text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">
              {{ folder.name }}
            </h4>
            <div class="space-y-1">
              <button 
                v-for="requestItem in folder.item" 
                :key="requestItem.name"
                @click="selectItem({ type: 'request', data: requestItem, folder: folder.name })" 
                class="flex items-center w-full px-3 py-2 rounded-lg text-sm transition-all group hover:bg-slate-100 dark:hover:bg-slate-700"
                :class="isActive('request', requestItem) === true ? 'bg-primary text-white shadow-md shadow-primary/20' : 'text-slate-600 dark:text-slate-400'"
              >
                <span 
                  class="text-[10px] font-bold px-1.5 py-0.5 rounded mr-2 uppercase min-w-[40px] text-center"
                  :class="getMethodBadgeClass(requestItem.request.method, isActive('request', requestItem))"
                >
                  {{ requestItem.request.method }}
                </span>
                <span class="truncate">{{ requestItem.name }}</span>
              </button>
            </div>
          </div>

          <div v-if="isLoading === true" class="flex flex-col items-center justify-center py-10 text-slate-400">
            <span class="loading loading-spinner loading-md mb-2"></span>
            <span class="text-xs">Loading docs...</span>
          </div>
        </nav>
      </aside>

      <main class="flex-1 overflow-y-auto bg-white dark:bg-slate-900 custom-scrollbar relative" ref="mainContentRef">
        <div v-if="tocItems.length > 0" class="lg:hidden absolute top-5 right-5 z-10">
          <div class="dropdown dropdown-end">
            <label tabindex="0" class="btn btn-circle btn-sm btn-ghost bg-white/80 dark:bg-slate-800/80 backdrop-blur shadow-sm border border-slate-200 dark:border-slate-700">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16m-7 6h7" />
              </svg>
            </label>
            <ul tabindex="0" class="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-52 border border-base-300">
              <li v-for="item in tocItems" :key="item.id">
                <a @click="scrollToElement(item.id)" :class="{'pl-6 text-xs': item.level > 1}">{{ item.text }}</a>
              </li>
            </ul>
          </div>
        </div>

        <div v-if="error !== undefined && error !== null && error !== ''" class="max-w-4xl mx-auto mt-12 p-8 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-center text-red-700 dark:text-red-400">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 class="font-bold">Documentation Error</h3>
            <p class="text-sm opacity-90">{{ error }}</p>
          </div>
          <button @click="fetchCollectionData" class="btn btn-sm btn-ghost ml-auto">Retry</button>
        </div>

        <div v-else-if="selectedItem === null && isLoading === false" class="flex flex-col items-center justify-center h-full text-slate-400">
          <div class="w-20 h-20 mb-4 opacity-20">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p class="text-lg font-medium">Select an endpoint to begin</p>
          <p class="text-sm">Browse the sidebar to see available API methods</p>
        </div>

        <div v-else-if="selectedItem !== null" class="max-w-6xl mx-auto px-6 py-8 md:px-12 md:py-10 pb-32">
          <div v-if="selectedItem.type === 'info'" class="prose prose-slate dark:prose-invert max-w-none">
            <h1 class="text-4xl font-extrabold tracking-tight mb-8">{{ selectedItem.data.name }}</h1>
            <div class="bg-white dark:bg-slate-800 p-8 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm" v-html="renderMarkdown(selectedItem.data.description)"></div>
          </div>

          <div v-else-if="selectedItem.type === 'request'" class="flex flex-wrap lg:flex-nowrap items-start">
            <div class="lg:w-8/12 space-y-12" style="padding-inline-end: 2rem;">
              <header>
                <div class="flex items-center space-x-2 text-primary font-bold text-xs uppercase tracking-widest mb-2">
                  <span>{{ selectedItem.folder }}</span>
                  <span class="mx-1">/</span>
                  <span>{{ selectedItem.data.name }}</span>
                </div>
                <h1 class="text-3xl font-extrabold tracking-tight mb-6 text-slate-900 dark:text-white">
                  {{ selectedItem.data.name }}
                </h1>
                
                <div class="flex items-center py-1.5 pl-4 pr-2 bg-slate-100 dark:bg-slate-800 rounded-full border border-slate-200 dark:border-slate-700 w-fit">
                  <span 
                    class="text-xs font-bold px-2.5 py-1 rounded-full uppercase mr-3"
                    :class="getMethodBadgeClass(selectedItem.data.request.method, false)"
                  >
                    {{ selectedItem.data.request.method }}
                  </span>
                  <code class="text-sm font-mono pr-5 text-slate-700 dark:text-slate-300">{{ getEndpointPath(selectedItem.data.request) }}</code>
                </div>
              </header>

              <section v-if="selectedItem.data.request.description !== undefined && selectedItem.data.request.description !== ''">
                <h2 id="description" class="text-lg font-bold mb-5 flex items-center border-b border-slate-200 dark:border-slate-800 pb-2">
                  <span class="mr-2 text-primary">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7" />
                    </svg>
                  </span>
                  Description
                </h2>
                <div class="text-slate-600 dark:text-slate-400 leading-relaxed" v-html="renderMarkdown(selectedItem.data.request.description)"></div>
              </section>

              <section v-if="selectedItem.data.request.url.variable?.length > 0">
                <h2 id="path-variables" class="text-lg font-bold mb-5 border-b border-slate-200 dark:border-slate-800 pb-2">Path Parameters</h2>
                <div class="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
                  <table class="table w-full">
                    <thead class="bg-slate-50 dark:bg-slate-800/50">
                      <tr>
                        <th class="text-xs font-bold uppercase text-slate-500">Parameter</th>
                        <th class="text-xs font-bold uppercase text-slate-500">Value</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
                      <tr v-for="variable in selectedItem.data.request.url.variable" :key="variable.key">
                        <td class="font-mono text-xs font-bold text-primary">{{ variable.key }}</td>
                        <td class="text-sm text-slate-600 dark:text-slate-400">{{ variable.value || '—' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section v-if="requestBodyFields.length > 0">
                <h2 id="body-parameters" class="text-lg font-bold mb-5 border-b border-slate-200 dark:border-slate-800 pb-2">Body Parameters</h2>
                <div class="space-y-4">
                  <div v-for="field in requestBodyFields" :key="field.name" class="flex p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                    <div class="flex-1">
                      <div class="flex items-center mb-1">
                        <span class="font-mono text-sm font-bold text-primary">{{ field.name }}</span>
                        <span class="ml-2 px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-[10px] font-bold uppercase text-slate-500">{{ field.type }}</span>
                      </div>
                      <p class="text-sm text-slate-600 dark:text-slate-400">{{ field.description || 'No description provided.' }}</p>
                    </div>
                  </div>
                </div>
              </section>

              <section v-if="selectedItem.data.request.header?.length > 0">
                <h2 id="headers" class="text-lg font-bold mb-5 border-b border-slate-200 dark:border-slate-800 pb-2">Request Headers</h2>
                <div class="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
                  <table class="table w-full">
                    <thead class="bg-slate-50 dark:bg-slate-800/50">
                      <tr>
                        <th class="text-xs font-bold uppercase text-slate-500">Header</th>
                        <th class="text-xs font-bold uppercase text-slate-500">Value</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
                      <tr v-for="header in selectedItem.data.request.header" :key="header.key">
                        <td class="font-mono text-xs font-bold text-slate-700 dark:text-slate-300">{{ header.key }}</td>
                        <td class="font-mono text-xs text-slate-500">{{ header.value }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>
            </div>

            <div class="lg:w-4/12" style="padding-inline-start: 2rem;">
              <div class="sticky top-24 space-y-8">
                <div class="rounded-2xl overflow-hidden shadow-2xl bg-slate-900 border border-slate-800">
                  <div class="flex items-center justify-between px-4 py-2 bg-slate-800 border-b border-slate-700">
                    <span class="text-[10px] font-bold uppercase text-slate-400 tracking-widest">Example Request</span>
                    <div class="flex">
                      <button 
                        v-for="lang in ['cURL', 'JS']" 
                        :key="lang"
                        @click="selectedCodeLang = lang"
                        class="px-2 py-1 rounded text-[10px] font-bold transition-colors mx-1"
                        :class="selectedCodeLang === lang ? 'bg-slate-700 text-white' : 'text-slate-500 hover:text-slate-300'"
                      >
                        {{ lang }}
                      </button>
                    </div>
                  </div>
                  <div class="p-5 overflow-x-auto">
                    <pre v-if="selectedCodeLang === 'cURL'" class="text-xs font-mono text-emerald-400"><code>{{ generateCurlSnippet() }}</code></pre>
                    <pre v-else class="text-xs font-mono text-sky-400"><code>{{ generateJsSnippet() }}</code></pre>
                  </div>
                </div>

                <div v-if="selectedItem.data.request.body?.raw !== undefined && selectedItem.data.request.body?.raw !== null" class="rounded-2xl overflow-hidden shadow-xl bg-slate-900 border border-slate-800">
                  <div class="flex items-center justify-between px-4 py-2 bg-slate-800 border-b border-slate-700">
                    <span class="text-[10px] font-bold uppercase text-slate-400 tracking-widest">Payload Schema</span>
                  </div>
                  <div class="p-5 overflow-x-auto">
                    <pre class="text-xs font-mono text-slate-300"><code>{{ formatJson(selectedItem.data.request.body.raw) }}</code></pre>
                  </div>
                </div>

                <div class="p-6 rounded-2xl bg-primary/5 border border-primary/20">
                  <h4 class="text-sm font-bold text-primary mb-2 flex items-center">
                    <span class="mr-2">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </span>
                    Pro Tip
                  </h4>
                  <p class="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                    Include your Bearer token in the <code>Authorization</code> header for all protected endpoints. 
                    You can generate API keys in the <strong>API Keys</strong> section.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <aside class="hidden 2xl:block w-56 h-full bg-slate-50 dark:bg-slate-900 px-6 py-8 border-l border-slate-200 dark:border-slate-800 shrink-0">
        <h5 class="text-xs font-bold uppercase text-slate-500 tracking-widest mb-4">On this page</h5>
        <ul v-if="tocItems.length > 0" class="space-y-3">
          <li v-for="item in tocItems" :key="item.id">
            <button 
              @click="scrollToElement(item.id)"
              class="block w-full text-left text-sm transition-all hover:translate-x-1"
              :class="item.level === 1 ? 'font-bold text-slate-700 dark:text-slate-300' : 'pl-4 text-slate-500 dark:text-slate-500 text-xs'"
            >
              {{ item.text }}
            </button>
          </li>
        </ul>
        <div v-else class="text-xs text-slate-400 italic">
          Select an endpoint to see page sections.
        </div>
      </aside>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, nextTick } from 'vue';
import axios from '@/api/axiosInstance';
import { globalState } from '@/globalState';
import Header from '@/components/Header.vue';
import { marked } from 'marked';
import hljs from 'highlight.js';
import { sanitizeHtml } from '@/shared/utils/sanitizeHtml';
import 'highlight.js/styles/github-dark.css';

const collectionData = ref(null);
const isLoading = ref(true);
const error = ref(null);
const selectedItem = ref(null);
const showApiSidebar = ref(true);
const searchQuery = ref('');
const selectedCodeLang = ref('cURL');
const tocItems = ref([]);
const mainContentRef = ref(null);

const getAuthToken = () => {
  if (typeof document === 'undefined') return '';
  const authCookie = document.cookie.split('; ').find(row => row.startsWith('authToken=') === true);
  if (authCookie !== undefined) {
    return authCookie.split('=')[1];
  }
  return '';
};

const getAuthHeaders = () => {
  const token = getAuthToken();
  if (token !== undefined && token !== '') {
    return { 'Authorization': 'Bearer ' + token };
  }
  return {};
};

const fetchCollectionData = async () => {
  isLoading.value = true;
  error.value = null;
  try {
    const response = await axios.get(globalState.apiEndpoint + "/api/docs/collection", {
      headers: getAuthHeaders(),
      withCredentials: true
    });
    collectionData.value = response.data;
    if (collectionData.value?.info !== undefined && collectionData.value?.info !== null) {
      selectItem({ type: 'info', data: collectionData.value.info });
    }
  } catch (err) {
    console.error("Error fetching documentation:", err);
    error.value = err.response?.data?.error || err.message || 'Failed to connect to documentation service.';
  } finally {
    isLoading.value = false;
  }
};

const selectItem = (item) => {
  selectedItem.value = item;
  nextTick(() => {
    if (mainContentRef.value !== null) {
      mainContentRef.value.scrollTop = 0;
    }
    generateToc();
  });
};

const isActive = (type, data = null) => {
  if (selectedItem.value === null || selectedItem.value.type !== type) return false;
  if (type === 'info') return true;
  if (data !== null && selectedItem.value.data !== undefined) {
    return selectedItem.value.data.name === data.name;
  }
  return false;
};

const getMethodBadgeClass = (method, isSelected) => {
  if (isSelected === true) return 'bg-white text-primary';
  const m = method?.toUpperCase();
  if (m === 'GET') return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
  if (m === 'POST') return 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400';
  if (m === 'PUT') return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
  if (m === 'DELETE') return 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400';
  return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400';
};

const filteredCollection = computed(() => {
  if (collectionData.value?.item === undefined) return [];
  if (searchQuery.value === "") return collectionData.value.item;
  
  const query = searchQuery.value.toLowerCase();
  return collectionData.value.item.map(folder => ({
    ...folder,
    item: folder.item.filter(req => 
      req.name.toLowerCase().includes(query) === true || 
      req.request?.url?.raw?.toLowerCase().includes(query) === true
    )
  })).filter(folder => folder.item.length > 0);
});

const getEndpointPath = (request) => {
  if (request?.url?.path !== undefined && request?.url?.path !== null) {
    return '/' + request.url.path.join('/');
  }
  return request?.url?.raw || '—';
};

const requestBodyFields = computed(() => {
  if (selectedItem.value?.data?.request?.body?.raw === undefined || selectedItem.value?.data?.request?.body?.raw === null) return [];
  try {
    const parsed = JSON.parse(selectedItem.value.data.request.body.raw);
    return Object.keys(parsed).map(key => ({
      name: key,
      type: typeof parsed[key],
      value: parsed[key]
    }));
  } catch (e) {
    return [];
  }
});

const generateCurlSnippet = () => {
  if (selectedItem.value?.data?.request === undefined || selectedItem.value?.data?.request === null) return "";
  const req = selectedItem.value.data.request;
  const method = req.method;
  const url = globalState.apiEndpoint + getEndpointPath(req);
  let curl = "curl -X " + method + " \"" + url + "\" \\\n";
  curl += "  -H \"Authorization: Bearer YOUR-TOKEN\" \\\n";
  curl += "  -H \"Content-Type: application/json\"";
  
  if (req.body?.raw !== undefined && req.body?.raw !== null) {
    curl += " \\\n  -d '" + req.body.raw.replace(/'/g, "'\\''") + "'";
  }
  return curl;
};

const generateJsSnippet = () => {
  if (selectedItem.value?.data?.request === undefined || selectedItem.value?.data?.request === null) return "";
  const req = selectedItem.value.data.request;
  const path = getEndpointPath(req);
  let js = "fetch(\"" + globalState.apiEndpoint + path + "\", {\n";
  js += "  method: \"" + req.method + "\",\n";
  js += "  headers: {\n";
  js += "    \"Authorization\": \"Bearer YOUR-TOKEN\",\n";
  js += "    \"Content-Type\": \"application/json\"\n";
  js += "  },\n";
  if (req.body?.raw !== undefined && req.body?.raw !== null) {
    js += "  body: JSON.stringify(" + req.body.raw + ")\n";
  }
  js += "})\n.then(res => res.json())\n.then(console.log);";
  return js;
};

const formatJson = (json) => {
  try {
    return JSON.stringify(typeof json === 'string' ? JSON.parse(json) : json, null, 2);
  } catch (e) {
    return json;
  }
};

const renderMarkdown = (text) => {
  if (text === undefined || text === null || text === "") {
    return "";
  }
  return sanitizeHtml(marked.parse(text));
};

const generateToc = () => {
  tocItems.value = [];
  nextTick(() => {
    if (mainContentRef.value === null) return;
    const headings = mainContentRef.value.querySelectorAll('h1, h2');
    tocItems.value = Array.from(headings).map(h => {
      if (h.id === "" || h.id === undefined || h.id === null) {
        h.id = 'heading-' + Math.random().toString(36).substring(2, 9);
      }
      return {
        id: h.id,
        text: h.textContent,
        level: parseInt(h.tagName[1])
      };
    });
  });
};

const scrollToElement = (id) => {
  const el = document.getElementById(id);
  if (el !== null) {
    el.scrollIntoView({ behavior: 'smooth' });
  }
};

const toggleApiSidebar = () => {
  showApiSidebar.value = showApiSidebar.value === false;
};

onMounted(() => {
  fetchCollectionData();
});

watch(selectedItem, () => {
  nextTick(() => {
    document.querySelectorAll('pre code').forEach((block) => {
      hljs.highlightElement(block);
    });
  });
});
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 5px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}
.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background: #475569;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

:deep(.prose) {
  color: inherit;
}
:deep(.prose h1), :deep(.prose h2), :deep(.prose h3) {
  color: inherit;
  font-weight: 800;
}
:deep(.prose code) {
  background-color: #f1f5f9;
  padding: 0.2rem 0.4rem;
  border-radius: 0.25rem;
  font-size: 0.85em;
}
.dark :deep(.prose code) {
  background-color: #1e293b;
}
</style>
